import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Clock, Play, CheckCircle, XCircle, Loader2 } from "lucide-react";
import { AppNav } from "@/components/AppNav";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const formatDuration = (seconds) => {
  if (!seconds && seconds !== 0) return "N/A";
  if (seconds < 60) return `${seconds}s`;
  if (seconds < 3600) return `${Math.floor(seconds / 60)}m ${seconds % 60}s`;
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  return `${h}h ${m}m`;
};

export default function TestHistory() {
  const navigate = useNavigate();
  const [executions, setExecutions] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchExecutions();
  }, []);

  const fetchExecutions = async () => {
    try {
      const response = await axios.get(`${API}/executions`, { withCredentials: true });
      const sorted = response.data.sort((a, b) => new Date(b.start_time) - new Date(a.start_time));
      setExecutions(sorted);
    } catch (error) {
      console.error('Failed to fetch executions:', error);
      toast.error('Failed to load history');
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'completed': return 'bg-green-500/10 text-green-400 border-green-500/20';
      case 'running': return 'bg-amber-500/10 text-amber-400 border-amber-500/20';
      case 'failed': return 'bg-red-500/10 text-red-400 border-red-500/20';
      default: return 'bg-slate-800 text-slate-300 border-slate-700';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'completed': return <CheckCircle className="w-5 h-5 text-green-400" />;
      case 'running': return <Loader2 className="w-5 h-5 text-amber-400 animate-spin" />;
      case 'failed': return <XCircle className="w-5 h-5 text-red-400" />;
      default: return <Clock className="w-5 h-5 text-slate-400" />;
    }
  };

  return (
    <div className="min-h-screen bg-[#f8f9fb] text-slate-900" data-testid="history-page">
      <div className="fixed inset-0 hero-glow" />
      <div className="fixed inset-0 opacity-5" 
        style={{
          backgroundImage: 'url("data:image/svg+xml,%3Csvg width="60" height="60" viewBox="0 0 60 60" xmlns="http://www.w3.org/2000/svg"-%3E%3Cg fill="none" fill-rule="evenodd"-%3E%3Cg fill="%23ffffff" fill-opacity="0.4"-%3E%3Cpath d="M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z"/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")'
        }}
      />

      <div className="relative z-10">
        <AppNav />

        {/* Main Content */}
        <div className="px-6 py-12 md:px-12">
          <div className="max-w-7xl mx-auto">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
            >
              <h1 className="text-4xl md:text-5xl font-extrabold tracking-tighter mb-4" style={{ fontFamily: 'Unbounded, sans-serif' }}>
                Execution History
              </h1>
              <p className="text-slate-400 mb-12">View all your test executions</p>

              {loading ? (
                <div className="flex items-center justify-center py-12">
                  <Loader2 className="w-12 h-12 text-[#00E599] animate-spin" />
                </div>
              ) : executions.length === 0 ? (
                <Card className="glass-card border-white/5 rounded-xl">
                  <CardContent className="p-12 text-center">
                    <Clock className="w-16 h-16 text-slate-600 mx-auto mb-4" />
                    <h3 className="text-xl font-semibold mb-2">No execution history</h3>
                    <p className="text-slate-400 mb-6">Run some tests to see execution history here</p>
                    <Button 
                      data-testid="go-to-dashboard-btn"
                      className="bg-[#00E599] text-black hover:bg-[#00CC88] rounded-sm font-semibold"
                      onClick={() => navigate('/dashboard')}
                    >
                      Go to Dashboard
                    </Button>
                  </CardContent>
                </Card>
              ) : (
                <div className="space-y-4">
                  {executions.map((execution, idx) => (
                    <motion.div
                      key={execution.id}
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: idx * 0.05 }}
                    >
                      <Card 
                        className="glass-card border-white/5 rounded-xl hover:border-white/10 transition-colors cursor-pointer"
                        onClick={() => navigate(`/execution/${execution.id}`)}
                        data-testid={`history-execution-${execution.id}`}
                      >
                        <CardContent className="p-6">
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-4 flex-1">
                              <div className="w-12 h-12 bg-[#00E599]/10 rounded-sm flex items-center justify-center">
                                {getStatusIcon(execution.status)}
                              </div>
                              <div className="flex-1">
                                <div className="flex items-center gap-3 mb-2">
                                  <p className="font-semibold">Execution</p>
                                  <Badge className={getStatusColor(execution.status)}>
                                    {execution.status}
                                  </Badge>
                                  {execution.result && (
                                    <Badge className="bg-slate-800 text-slate-300 border-slate-700">
                                      {execution.result}
                                    </Badge>
                                  )}
                                </div>
                                <div className="flex items-center gap-4 text-sm text-slate-400">
                                  <span>ID: {execution.id.slice(0, 8)}...</span>
                                  <span>•</span>
                                  <span>{new Date(execution.start_time).toLocaleString()}</span>
                                  {execution.end_time && (
                                    <>
                                      <span>•</span>
                                      <span>Duration: {formatDuration(Math.round((new Date(execution.end_time) - new Date(execution.start_time)) / 1000))}</span>
                                    </>
                                  )}
                                </div>
                              </div>
                            </div>
                            <Button 
                              data-testid={`view-execution-btn-${execution.id}`}
                              size="sm"
                              className="bg-[#00E599] text-black hover:bg-[#00CC88] rounded-sm"
                              onClick={(e) => {
                                e.stopPropagation();
                                navigate(`/execution/${execution.id}`);
                              }}
                            >
                              <Play className="w-4 h-4 mr-1" />
                              View
                            </Button>
                          </div>
                        </CardContent>
                      </Card>
                    </motion.div>
                  ))}
                </div>
              )}
            </motion.div>
          </div>
        </div>
      </div>
    </div>
  );
}